import { FC } from 'react'
import cx from 'classnames'
import * as Popover from '@radix-ui/react-popover'
import { useTranslation } from 'next-i18next'

import { PrimaryButton, TertiaryButton, InfoTooltip } from 'ui'

type FilterProps = {
  heading?: string
  tooltip?: string
  buttonText: string
  onOpenChange?: (open: boolean) => void
  onClear?: () => void
  isActive?: boolean
}

const Filter: FC<FilterProps> = ({
  children,
  heading,
  tooltip,
  buttonText,
  onOpenChange,
  onClear,
  isActive,
}) => {
  const { t } = useTranslation('common')

  return (
    <Popover.Root onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          className={cx(
            'rounded-sm px-14 py-12 font-semibold leading-snug ring-1 ring-grey-3',
            'focus:ring-teal-3',
            'state-open:bg-teal-1 state-open:text-teal-3 state-open:ring-teal-3',
            {
              'text-teal-3 hover:bg-teal-1 hover:ring-teal-3': !isActive,
              'bg-teal-3 text-white ring-teal-3': isActive,
            }
          )}
        >
          {buttonText}
        </button>
      </Popover.Trigger>
      <Popover.Content
        onOpenAutoFocus={tooltip ? (e) => e.preventDefault() : () => null}
        sideOffset={12}
        align="start"
        className="max-w-[360px] overflow-hidden rounded-md bg-white shadow-popover ring-1 ring-grey-3"
      >
        {heading ? (
          <div className="border-b px-18 py-12">
            <span className="font-semibold">{heading}</span>
            {tooltip ? (
              <InfoTooltip
                side="right"
                className="pointer-events-auto relative ml-8"
              >
                {tooltip}
              </InfoTooltip>
            ) : null}
          </div>
        ) : null}
        <div className="p-18">{children}</div>
        <div className="flex flex-row-reverse items-center justify-start space-x-16 space-x-reverse border-t p-10">
          <Popover.Close asChild>
            <PrimaryButton>{t('ui.done')}</PrimaryButton>
          </Popover.Close>
          {onClear ? (
            <TertiaryButton onClick={onClear}>{t('ui.clear')}</TertiaryButton>
          ) : null}
        </div>
      </Popover.Content>
    </Popover.Root>
  )
}

export default Filter
